<script lang="ts">
	import { t } from '$lib/stores/i18n';
	import type { LoginRequest } from '$lib/stores/model/user';
	import { authStore, login, socialLoginStart } from '$lib/stores/user';
	import { createEventDispatcher } from 'svelte';

	import ForgotPasswordCard from './ForgotPasswordCard.svelte';
	import RegistrationCard from './RegistrationCard.svelte';

	export let isOpen: boolean = false;
	export let onClose = () => {};
	export let onLoginSuccess = (user: any) => {};
	export let isRegisterMode: boolean = false;
	let isForgotPasswordMode: boolean = false;

	const dispatch = createEventDispatcher();

	let username = '';
	let password = '';
	let errorMessage = '';
	let validationErrors: { [key: string]: string[] } = {};
	let showPassword = false;
	let mouseDownInsideModal = false;

	// Subscribe to auth store for loading state
	$: isLoading = $authStore.isLoading;

	// Clear errors when user types
	$: if (username || password) {
		errorMessage = '';
		validationErrors = {};
	}

	async function handleLogin() {
		if (!username || !password) {
			errorMessage = $t('login.errors.emptyFields');
			return;
		}

		errorMessage = '';
		validationErrors = {};

		try {
			const credentials: LoginRequest = {
				email: username,
				password: password
			};

			const response = await login(credentials);

			// Call success callbacks
			onLoginSuccess(response.user);
			handleClose();
			//เช็คหน่อย ว่า url เป็น 3dprintingservice  ป่าว ถ้าใช่ reload page
			if (window.location.href.includes('3dprintingservice')) {
				window.location.href = '/3dprintingservice';
			}

			dispatch('login-success', response.user);
		} catch (error: any) {
			// Handle different types of errors
			if (error.response?.data) {
				const errorData = error.response.data;

				if (errorData.errors) {
					// Validation errors
					validationErrors = errorData.errors;
					errorMessage = errorData.message || $t('login.errors.checkInformation');
				} else {
					// General error
					errorMessage = errorData.message || $t('login.errors.loginFailed');
				}
			} else {
				errorMessage = $t('login.errors.connectionError');
			}
		}
	}

	async function handleGoogleLogin() {
		try {
			await socialLoginStart('google');
		} catch (error) {
			console.error('Google login error:', error);
			errorMessage = $t('login.errors.googleLoginError');
		}
	}

	async function handleAppleLogin() {
		try {
			await socialLoginStart('apple');
		} catch (error) {
			console.error('Apple login error:', error);
			errorMessage = $t('login.errors.appleLoginError');
		}
	}

	async function handleFacebookLogin() {
		try {
			await socialLoginStart('facebook');
		} catch (error) {
			console.error('Facebook login error:', error);
			errorMessage = $t('login.errors.facebookLoginError');
		}
	}

	async function handleLineLogin() {
		try {
			await socialLoginStart('line');
		} catch (error) {
			console.error('LINE login error:', error);
			errorMessage = $t('login.errors.lineLoginError');
		}
	}

	function handleRegister() {
		isRegisterMode = true;
	}

	function handleKeyPress(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			handleLogin();
		}
	}

	function handleClose() {
		mouseDownInsideModal = false; // reset state when modal closes
		isOpen = false;
		isRegisterMode = false;
		isForgotPasswordMode = false;
		onClose();
	}

	function handleForgotPassword() {
		isForgotPasswordMode = true;
	}

	function handleForgotPasswordSuccess() {
		isForgotPasswordMode = false;
		// Optionally show success message or auto-login
	}

	// ฟังก์ชันจัดการ mouse down
	function handleMouseDown(event: MouseEvent) {
		// เช็คว่า mouse down เริ่มจาก modal หรือไม่
		const modalContainer = (event.currentTarget as HTMLElement).querySelector('.modal-container');
		if (modalContainer && modalContainer.contains(event.target as Node)) {
			mouseDownInsideModal = true;
		} else {
			mouseDownInsideModal = false;
		}
	}

	// ฟังก์ชันจัดการ overlay click
	function handleOverlayClick(event: MouseEvent) {
		// ถ้า mouse down เริ่มจากใน modal แล้ว up ข้างนอก ไม่ให้ปิด modal
		if (mouseDownInsideModal) {
			mouseDownInsideModal = false; // reset state
			return;
		}

		// ถ้า mouse down และ up ทั้งคู่อยู่ข้างนอก modal ให้ปิด modal
		if (event.target === event.currentTarget) {
			handleClose();
		}
	}
</script>

{#if isOpen}
	{#if isForgotPasswordMode}
		<ForgotPasswordCard
			onClose={handleClose}
			isOpen={isForgotPasswordMode}
			onSuccess={handleForgotPasswordSuccess}
		/>
	{:else if isRegisterMode}
		<RegistrationCard
			onClose={handleClose}
			isOpen={isRegisterMode}
			onClickLogin={() => (isRegisterMode = false)}
			onRegisterSuccess={onLoginSuccess}
		/>
	{:else}
		<div
			class="modal-overlay"
			on:mousedown={handleMouseDown}
			on:click={handleOverlayClick}
			on:keydown={(e) => e.key === 'Escape' && handleClose()}
			role="dialog"
			aria-modal="true"
			tabindex="-1"
		>
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<div class="modal-container" on:click|stopPropagation role="button" tabindex="0">
				<div class="modal-header">
					<button
						class="close-button"
						on:click={handleClose}
						aria-label={$t('login.ariaLabels.close')}
					>
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
							<path d="M6 6L18 18M6 18L18 6" />
						</svg>
					</button>
				</div>

				<div class="modal-content">
					<!-- Shield Icon -->
					<div class="icon-container">
						<img src="/assets/img/icons/login_secue.png" alt="Shield Icon" />
					</div>

					<h2 class="modal-title">{$t('login.title')}</h2>

					<form class="login-form" on:submit|preventDefault={handleLogin}>
						<div class="form-group">
							<input
								type="text"
								bind:value={username}
								placeholder={$t('login.usernamePlaceholder')}
								class="form-input {validationErrors.email ? 'error' : ''}"
								on:keypress={handleKeyPress}
								disabled={isLoading}
							/>
							{#if validationErrors.email}
								<div class="field-error">
									{#each validationErrors.email as error}
										<p>{error}</p>
									{/each}
								</div>
							{/if}
						</div>

						<div class="form-group">
							<div class="password-input-container">
								<input
									style="width:336px;"
									type={showPassword ? 'text' : 'password'}
									bind:value={password}
									placeholder={$t('login.passwordPlaceholder')}
									class="form-input {validationErrors.password ? 'error' : ''}"
									on:keypress={handleKeyPress}
									disabled={isLoading}
								/>
								<button
									type="button"
									class="password-toggle"
									on:click={() => (showPassword = !showPassword)}
									aria-label={showPassword
										? $t('login.ariaLabels.hidePassword')
										: $t('login.ariaLabels.showPassword')}
								>
									{#if showPassword}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path
												d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94L17.94 17.94z"
											/>
											<path d="M1 1l22 22" />
											<path
												d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19L9.9 4.24z"
											/>
										</svg>
									{:else}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
											<circle cx="12" cy="12" r="3" />
										</svg>
									{/if}
								</button>
							</div>
							{#if validationErrors.password}
								<div class="field-error">
									{#each validationErrors.password as error}
										<p>{error}</p>
									{/each}
								</div>
							{/if}
						</div>

						{#if errorMessage}
							<div class="error-message">
								{errorMessage}
							</div>
						{/if}

						<div class="forgot-password-link">
							<button type="button" class="link-button" on:click={handleForgotPassword}>
								{$t('login.forgotPassword')}
							</button>
						</div>

						<button type="submit" class="login-button" disabled={isLoading}>
							{#if isLoading}
								<div class="spinner"></div>
								{$t('login.loggingIn')}
							{:else}
								{$t('login.loginButton')}
							{/if}
						</button>
					</form>

					<div class="register-link">
						<span>{$t('login.dontHaveAccount')} </span>
						<button class="link-button" on:click={handleRegister}>{$t('login.register')}</button>
					</div>

					<div class="divider">
						<div class="divider-line"></div>
					</div>

					<div class="social-login">
						<!-- <button class="google-button" on:click={handleGoogleLogin} disabled={isLoading}>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
								<path
									d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									fill="#4285F4"
								/>
								<path
									d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									fill="#34A853"
								/>
								<path
									d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
									fill="#FBBC05"
								/>
								<path
									d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
									fill="#EA4335"
								/>
							</svg>
							Continue with Google
						</button> -->

						<button class="line-button" on:click={handleLineLogin} disabled={isLoading}>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none">
								<path
									d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"
									fill="#00C300"
								/>
							</svg>
							{$t('login.continueWithLine')}
						</button>
					</div>

					<div class="social-icons">
						<!-- <button class="social-icon apple-icon" on:click={handleAppleLogin} disabled={isLoading} aria-label="Login with Apple">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
								<path
									d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
								/>
							</svg>
						</button> -->
						<!-- <button
							class="social-icon facebook-icon"
							on:click={handleFacebookLogin}
							disabled={isLoading}
							aria-label="Login with Facebook"
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
								<path
									d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
								/>
							</svg>
						</button> -->
						<button
							class="social-icon line-icon"
							on:click={handleLineLogin}
							disabled={isLoading}
							aria-label={$t('login.ariaLabels.loginWithLine')}
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
								<path
									d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"
									fill="#00C300"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>
	{/if}
{/if}

<style>
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.8);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	.modal-container {
		background: #1a1a1a;
		border-radius: 16px;
		max-width: 400px;
		width: 90%;
		max-height: 90vh;
		overflow-y: auto;
		position: relative;
	}

	.modal-header {
		display: flex;
		justify-content: flex-end;
		padding: 16px;
	}

	.close-button {
		background: none;
		border: none;
		color: #ffffff;
		cursor: pointer;
		padding: 8px;
		border-radius: 50%;
		transition: background-color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.close-button:hover {
		background: #333;
	}

	.modal-content {
		padding: 0 32px 32px 32px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 24px;
	}

	.icon-container {
		display: flex;
		justify-content: center;
		margin-bottom: 8px;
	}

	.modal-title {
		color: #ffffff;
		font-size: 20px;
		font-weight: 600;
		margin: 0;
		text-align: center;
	}

	.login-form {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.form-group {
		display: flex;
		flex-direction: column;
	}

	.form-input {
		background: #2a2a2a;
		border: 1px solid #404040;
		border-radius: 8px;
		padding: 12px 16px;
		color: #ffffff;
		font-size: 14px;
		transition: border-color 0.2s;
	}

	.form-input::placeholder {
		color: #888;
	}

	.form-input:focus {
		outline: none;
		border-color: #4a6cf7;
	}

	.form-input:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.form-input.error {
		border-color: #ff4444;
		background: rgba(255, 68, 68, 0.05);
	}

	.password-input-container {
		position: relative;
	}

	.password-toggle {
		position: absolute;
		right: 12px;
		top: 50%;
		transform: translateY(-50%);
		background: none;
		border: none;
		color: #888;
		cursor: pointer;
		padding: 4px;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
		transition: color 0.2s;
	}

	.password-toggle:hover {
		color: #4a6cf7;
	}

	.password-toggle:focus {
		outline: none;
		color: #4a6cf7;
	}

	.password-input-container .form-input {
		padding-right: 45px;
	}

	.field-error {
		color: #ff4444;
		font-size: 12px;
		margin-top: 4px;
	}

	.field-error p {
		margin: 0;
		padding: 2px 0;
	}

	.error-message {
		color: #ff4444;
		font-size: 14px;
		text-align: center;
		padding: 8px;
		background: rgba(255, 68, 68, 0.1);
		border-radius: 4px;
		border: 1px solid rgba(255, 68, 68, 0.3);
	}

	.login-button {
		background: #4a6cf7;
		border: none;
		border-radius: 8px;
		padding: 12px 24px;
		color: #ffffff;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.login-button:hover:not(:disabled) {
		background: #3a5ae7;
	}

	.login-button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.spinner {
		width: 16px;
		height: 16px;
		border: 2px solid #ffffff;
		border-top: 2px solid transparent;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.register-link {
		color: #888;
		font-size: 14px;
		text-align: center;
	}

	.link-button {
		background: none;
		border: none;
		color: #4a6cf7;
		cursor: pointer;
		text-decoration: underline;
		font-size: 14px;
	}

	.link-button:hover {
		color: #3a5ae7;
	}

	.forgot-password-link {
		text-align: right;
		margin-top: -8px;
	}

	.forgot-password-link .link-button {
		font-size: 12px;
		padding: 0;
		text-decoration: none;
	}

	.divider {
		width: 100%;
		position: relative;
		height: 1px;
		background: #404040;
		margin: 8px 0;
	}

	.divider-line {
		width: 100%;
		height: 1px;
		background: #404040;
	}

	.social-login {
		width: 100%;
	}

	/* .google-button {
		width: 100%;
		background: #ffffff;
		border: 1px solid #dadce0;
		border-radius: 8px;
		padding: 12px 16px;
		color: #3c4043;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
	} */

	/* .google-button:hover:not(:disabled) {
		background: #f8f9fa;
		box-shadow: 0 1px 2px 0 rgba(60, 64, 67, 0.3);
	}

	.google-button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	} */

	.line-button {
		width: 100%;
		background: #00c300;
		border: 1px solid #00b300;
		border-radius: 8px;
		padding: 12px 16px;
		color: #ffffff;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		margin-top: 12px;
	}

	.line-button:hover:not(:disabled) {
		background: #00b300;
		box-shadow: 0 1px 2px 0 rgba(0, 195, 0, 0.3);
	}

	.line-button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.social-icons {
		display: flex;
		gap: 16px;
		justify-content: center;
	}

	.social-icon {
		width: 48px;
		height: 48px;
		border-radius: 50%;
		border: none;
		cursor: pointer;
		transition: all 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	/* .apple-icon {
		background: #000000;
		color: #ffffff;
	}

	.apple-icon:hover:not(:disabled) {
		background: #333333;
	}

	.facebook-icon {
		background: #1877f2;
		color: #ffffff;
	}

	.facebook-icon:hover:not(:disabled) {
		background: #166fe5;
	} */

	.line-icon {
		background: #00c300;
		color: #ffffff;
	}

	.line-icon:hover:not(:disabled) {
		background: #00b300;
	}

	.social-icon:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
</style>
